import { Injectable } from "@angular/core";
import { PlessDialogServiceSub } from "src/app/commons/kymw00/pless-dialog-service-sub";
import { PlessJsonResponderSub } from "src/app/commons/kymw00/pless-json-responder-sub.service";
import { PlessHttp } from "src/app/shared/http/pless-http.service";
import { PlessRequest } from "src/app/shared/vo/pless-request.model";
import { KYMW7500Model } from "../kymw7500.model";
import { GoodsForCheckPlanNumModel } from "../vo/goods-for-check-plan-num.model";
import { PlanNumSearchParamsModel } from "../vo/plan-num-search-params.model";
import { PredictDiffSign } from "../vo/predict-diff-sign.model";

interface GetGoodsListForCheckPlanNumResponseItem {
  OrderCode: string;
  MediumType: string;
  CheckFlag: string;
  PredictPlanNum: string | null;
  PlanNum: string | null;
  PredictDiff: string | null;
  SupplyPrice: string | null;
  FeatureCode: string | null;
  FeatureName: string | null;
  PCode: string | null;
  GoodsGuideName: string | null;
  GoodsStandardName: string | null;
  PricePattern: string | null;
  PricePromotion: string | null;
  PricePromotionName: string | null;
  AppearDiv: string | null;
  AppearDivName: string | null;
  KomaPromotion: string | null;
  KomaPromotionName: string | null;
  PromotionMark: string | null;
  PromotionMarkName: string | null;
  ThisTimeSupplyPrice: string | null;
  ThisTimeCostPrice: string | null;
}

export type GetGoodsListForCheckPlanNumResponse =
  GetGoodsListForCheckPlanNumResponseItem[];

/**
 * 企画商品特集情報を取得するサービス
 *
 * @export GetGoodsListForCheckPlanNumService
 * @class GetGoodsListForCheckPlanNumService
 * @extends {PlessJsonResponderSub}
 */
@Injectable()
export class GetGoodsListForCheckPlanNumService extends PlessJsonResponderSub<GetGoodsListForCheckPlanNumResponse> {
  static readonly UNCHECKED = '0';
  static readonly CHECKED = '1';
  static convert(from: GetGoodsListForCheckPlanNumResponseItem) {
    const predictDiff = from.PredictDiff != null ?
      { absValue: Math.abs(Number(from.PredictDiff)).toString(), sign: PredictDiffSign.from(Number(from.PredictDiff)) } :
      { absValue: '', sign: PredictDiffSign.Zero };

    return new GoodsForCheckPlanNumModel(
      from.OrderCode,
      from.MediumType,
      from.CheckFlag === this.CHECKED ? true : false,
      from.PredictPlanNum,
      from.PlanNum,
      predictDiff,
      from.SupplyPrice,
      { code: from.FeatureCode, label: this.getLabel(from.FeatureCode, from.FeatureName) },
      from.PCode,
      from.GoodsGuideName,
      from.GoodsStandardName,
      from.PricePattern,
      { code: from.PricePromotion, label: this.getLabel(from.PricePromotion, from.PricePromotionName) },
      { code: from.AppearDiv, label: this.getLabel(from.AppearDiv, from.AppearDivName) },
      { code: from.KomaPromotion, label: this.getLabel(from.KomaPromotion, from.KomaPromotionName) },
      { code: from.PromotionMark, label: this.getLabel(from.PromotionMark, from.PromotionMarkName) },
      from.ThisTimeSupplyPrice,
      from.ThisTimeCostPrice
    );
  }

  static getLabel(code: string, name: string) {
    if (!code || !name) {
      return '';
    }
    return `${code}:${name}`;
  }

  private vo: PlanNumSearchParamsModel;
  private model: KYMW7500Model;
  private successFunc: () => void;

  constructor(
    protected plessHttp: PlessHttp,
    protected dialogService: PlessDialogServiceSub
  ) {
    super(plessHttp, dialogService);
  }

  /**
   * bodyにセットする値を返す
   *
   * @private
   * @returns {PlessRequest} リクエストJSON
   * @memberof GetGoodsListForCheckPlanNumService
   */
  private getBody(): PlessRequest {
    const originalParams = {
      OrderCode: this.vo.orderCode,
      MediumType: this.vo.mediumType,
      FeatureCode: this.vo.featureCode,
      BuyerCode: this.vo.buyerCode,
      Folio: this.vo.folio,
      ClassCode: this.vo.classCode,
      MiddleClassCode: this.vo.middleClassCode,
      BoundaryNum: !!this.vo.boundaryNum ?
        this.vo.boundaryNum :
        this.vo.boundaryNumDetail,
      CheckFlag: this.vo.checkFlag ?
        GetGoodsListForCheckPlanNumService.CHECKED :
        GetGoodsListForCheckPlanNumService.UNCHECKED
    };
    const parameter = this.removePropertiesOfEmptyValue(originalParams);
    return new PlessRequest({ parameter });
  }

  private removePropertiesOfEmptyValue(obj: { [key: string]: string; }) {
    const result = Object.keys(obj).reduce((currentObj, key) => {
      const value = obj[key];
      if (!value) {
        return currentObj;
      }
      return { ...currentObj, [key]: value };
    }, {});
    return result;
  }

  /**
   * API呼び出し
   *
   * @param model
   * @memberof GetGoodsListForCheckPlanNumService
   */
  execute(vo: PlanNumSearchParamsModel, model: KYMW7500Model, successFunc?: () => void) {
    this.vo = vo;
    this.model = model;
    this.successFunc = successFunc;
    this.send('getGoodsListForCheckPlanNum', this.getBody());
  }

  /**
   * API通信成功時の処理
   *
   * @param {*} data レスポンスデータ 
   * @memberof GetGoodsListForCheckPlanNumService 
   */
  success(data: GetGoodsListForCheckPlanNumResponse) {
    this.model.goodsList = data.map(goods => GetGoodsListForCheckPlanNumService.convert(goods));

    if (!!this.successFunc) {
      this.successFunc();
    }
  }

} 